
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5955v13kamldd1564rg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: 'Email',
      value: 'nachteulecreation.contact@gmail.com',
      link: 'mailto:nachteulecreation.contact@gmail.com'
    },
    {
      icon: 'ri-phone-line',
      title: 'Phone',
      value: '080-3016-6394',
      link: 'tel:08030166394',
      note: '※We do not accept inquiries by phone.'
    },
    {
      icon: 'ri-global-line',
      title: 'Language',
      value: 'JAPANESE / ENGLISH',
      link: null
    }
  ];

  const hairMakeSocial = [
    { icon: 'ri-instagram-line', link: 'https://instagram.com', name: 'Instagram' },
    { icon: 'ri-twitter-x-line', link: 'https://twitter.com', name: 'Twitter' },
    { icon: 'ri-facebook-line', link: 'https://facebook.com', name: 'Facebook' }
  ];

  const musicSocial = [
    { icon: 'ri-youtube-line', link: 'https://youtube.com', name: 'YouTube' },
    { icon: 'ri-spotify-line', link: 'https://spotify.com', name: 'Spotify' },
    { icon: 'ri-soundcloud-line', link: 'https://soundcloud.com', name: 'SoundCloud' }
  ];

  return (
    <section id="contact" className="py-24 px-6 bg-gradient-to-b from-black to-slate-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-wider">CONTACT</h2>
          <div className="w-24 h-1 bg-purple-500 mx-auto"></div>
          <p className="mt-6 text-lg text-gray-300">Feel free to reach out anytime</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              <div className="space-y-4">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="flex items-center p-4 bg-slate-800/40 rounded-lg border border-purple-500/20 hover:border-purple-500/60 transition-all duration-300"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-purple-600/20 rounded-lg mr-4">
                      <i className={`${info.icon} text-2xl text-purple-400`}></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">{info.title}</p>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-white hover:text-purple-400 transition-colors cursor-pointer"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-white">{info.value}</p>
                      )}
                      {info.note && (
                        <p className="text-xs text-gray-500 mt-1">{info.note}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-6">Follow Us - Hair & Makeup</h3>
              <div className="flex gap-4">
                {hairMakeSocial.map((social, index) => (
                  <a
                    key={index}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center bg-slate-800/40 hover:bg-purple-600 rounded-lg border border-purple-500/20 hover:border-purple-500 transition-all duration-300 cursor-pointer group"
                    title={social.name}
                  >
                    <i className={`${social.icon} text-xl text-purple-400 group-hover:text-white transition-colors`}></i>
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-6">Follow Us - Music</h3>
              <div className="flex gap-4">
                {musicSocial.map((social, index) => (
                  <a
                    key={index}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center bg-slate-800/40 hover:bg-purple-600 rounded-lg border border-purple-500/20 hover:border-purple-500 transition-all duration-300 cursor-pointer group"
                    title={social.name}
                  >
                    <i className={`${social.icon} text-xl text-purple-400 group-hover:text-white transition-colors`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">
                  Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-slate-800/50 border border-purple-500/30 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-sm text-white"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Email Address <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-slate-800/50 border border-purple-500/30 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-sm text-white"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium mb-2">
                  Desired Service <span className="text-red-400">*</span>
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-slate-800/50 border border-purple-500/30 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-sm text-white cursor-pointer"
                >
                  <option value="">Please select</option>
                  <option value="hair-make">Hair &amp; Makeup</option>
                  <option value="music-production">Music Production</option>
                  <option value="both">Both</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-2">
                  Message <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-purple-500/30 rounded-lg focus:outline-none focus:border-purple-500 transition-colors text-sm text-white resize-none"
                  placeholder="Please enter your message (up to 500 characters)"
                ></textarea>
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {formData.message.length} / 500
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-600/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
                  <i className="ri-check-line mr-2"></i>
                  Your message has been sent. Thank you!
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-600/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  Failed to send. Please try again.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-medium rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Sending...
                </>
                ) : (
                <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      <footer className="mt-24 pt-12 border-t border-purple-500/20">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="https://static.readdy.ai/image/6902dd986b6e328bf9bb68047035d84f/5fb36d5e9028b47c0fac686a51ca35fe.png" 
              alt="NACHTEULE CREATION" 
              className="h-16 w-16 object-contain"
            />
          </div>
          <p className="text-gray-400 text-sm mb-4">
            &copy; 2025 NACHTEULE CREATION Inc. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-purple-400 hover:text-purple-300 text-sm transition-colors cursor-pointer"
          >
            Website Builder
          </a>
        </div>
      </footer>
    </section>
  );
}
